import { Bell } from "lucide-react";
import { memo, useEffect, useRef, useState } from "react";
import { FaMoneyCheckDollar, FaCopy } from "react-icons/fa6"; // Added FaCopy
import { Link, useNavigate } from "react-router-dom";
import LoaderSpiner from "../../Components/LoaderSpiner";
import ProfileDropDown from "../../Components/ProfileDropDown/ProfileDropDown";
import RequestsComponent from "../../Components/RequestsComponent/RequestsComponent";
import { toast, ToastContainer } from "react-toastify"; // Import toast
import "react-toastify/dist/ReactToastify.css"; // Import toast CSS
import styles from "./HeaderBar.module.css";

const HeaderBar = () => {
  const [authData, setAuthData] = useState({
    user: { name: "Guest", email: "No email", role: "guest", domain: "domain" },
    message: "",
    token: "",
  });
  const [role, setRole] = useState(
    localStorage.getItem("role")?.trim().toLowerCase() || "guest"
  );
  const [rolebased, setRolebased] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [showCredit, setShowCredit] = useState(false);
  const [balanceData, setBalanceData] = useState({
    balance: 0,
    totalRecharges: 0,
    totalRechargedAmount: 0,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const userInfoRef = useRef(null);
  const navigate = useNavigate();

  // Normalize role to handle common typos
  const normalizeRole = (rawRole) => {
    const roleLower = rawRole?.trim().toLowerCase() || "guest";
    if (roleLower === "vender") return "vendor";
    return roleLower;
  };

  // Handle localStorage changes and initialize authData/role
  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === "role" || event.key === "authData") {
        console.log("Storage event detected:", event.key, event.newValue);
        const newRole = normalizeRole(localStorage.getItem("role"));
        setRole(newRole);
        const storedData = localStorage.getItem("authData");
        try {
          const parsedData = storedData ? JSON.parse(storedData) : {};
          const userRole = normalizeRole(parsedData.user?.role || newRole);
          setAuthData({
            user: parsedData.user
              ? { ...parsedData.user, role: userRole }
              : {
                name: "Guest",
                email: "No email",
                role: userRole,
                domain: "domain",
              },
            message: parsedData.message || "",
            token: parsedData.token || "",
          });
          if (!["vendor", "participant", "owner", "guest", "electionparty"].includes(userRole)) {
            console.warn(`Unrecognized role: ${userRole}`);
          }
        } catch (error) {
          console.error("Error parsing authData:", error);
          setAuthData({
            user: {
              name: "Guest",
              email: "No email",
              role: newRole,
              domain: "domain",
            },
            message: "",
            token: "",
          });
        }
      }
    };

    handleStorageChange({ key: "authData" });
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  // Update rolebased based on role
  useEffect(() => {
    const isRoleBased = ["vendor", "participant", "subowner", "electionparty"].includes(role);
    setRolebased(isRoleBased);
    console.log("Role:", role, "Rolebased:", isRoleBased);
  }, [role]);

  // Listen for balance change events from other components
  useEffect(() => {
    const handleBalanceChange = () => {
      console.log("Balance change event detected");
      setRefreshTrigger((prev) => prev + 1); // Trigger balance re-fetch
    };

    window.addEventListener("balanceChanged", handleBalanceChange);
    return () =>
      window.removeEventListener("balanceChanged", handleBalanceChange);
  }, []);

  // Fetch balance data
  useEffect(() => {
    if (rolebased && authData.token) {
      const fetchBalance = async () => {
        setIsLoading(true);
        setError(null);
        console.log("Fetching balance with token:", authData.token);
        try {
          const response = await fetch(
            `https://prod.toxboxai.com/api/recharge/balance?_t=${Date.now()}`,
            {
              method: "GET",
              headers: {
                Authorization: `Bearer ${authData.token}`,
                "Content-Type": "application/json",
              },
            }
          );

          if (!response.ok) {
            if (response.status === 401) {
              localStorage.removeItem("authData");
              localStorage.removeItem("role");
              navigate("/login");
              throw new Error("Unauthorized: Please log in again");
            }
            throw new Error(`HTTP error! Status: ${response.status}`);
          }

          const result = await response.json();
          console.log("Balance API response:", result);

          if (result.success) {
            setBalanceData(result.data);
          } else {
            setError("Failed to fetch balance data: Invalid response");
          }
        } catch (error) {
          console.error("Error fetching balance:", error);
          setError(`Error fetching balance: ${error.message}`);
        } finally {
          setIsLoading(false);
        }
      };

      fetchBalance();
    } else {
      console.log(
        "API not called. Rolebased:",
        rolebased,
        "Token:",
        authData.token
      );
    }
  }, [rolebased, authData.token, refreshTrigger, navigate]);

  // Handle outside click to close popups
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (userInfoRef.current && !userInfoRef.current.contains(event.target)) {
        setShowNotifications(false);
        setShowProfile(false);
        setShowCredit(false);
      }
    };
    document.addEventListener("click", handleOutsideClick);
    return () => document.removeEventListener("click", handleOutsideClick);
  }, []);

  const { user } = authData;

  const notifications = [
    { id: 1, message: "New project assigned to you", time: "2 hours ago" },
    { id: 2, message: "Meeting scheduled for tomorrow", time: "5 hours ago" },
    { id: 3, message: "Task deadline approaching", time: "1 day ago" },
  ];

  // Toggle popup functions
  const toggleNotifications = () => {
    setShowNotifications(!showNotifications);
    setShowProfile(false);
    setShowCredit(false);
  };

  const toggleProfile = () => {
    setShowProfile(!showProfile);
    setShowNotifications(false);
    setShowCredit(false);
  };

  const toggleCredit = () => {
    setShowCredit(!showCredit);
    setShowNotifications(false);
    setShowProfile(false);
  };

  const handleRetry = () => {
    setError(null);
    setRefreshTrigger((prev) => prev + 1);
  };


  const handleCopyUserId = () => {
    const userId = user?.id;               // <-- changed from _id to id
    if (!userId) {
      toast.error("User ID not available");
      return;
    }
    navigator.clipboard.writeText(userId).then(() => {
      toast.success("User ID copied to clipboard!");
    }).catch(() => {
      toast.error("Failed to copy User ID");
    });
  };

  const roleLabels = {
    vendor: "Client",
    participant: "Sales Person",
    owner: "Admin",
    subowner: "Sub Admin",
  };

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} />
      <header className={styles.header}>
        <div className={styles.container}>
          <div className={styles.logo}>
            <h1>Hello {user?.name || "Guest"}</h1>
            {role === "vendor" ? (
              <span className={styles.userEmail}>
                ({user?.domain || "Domain"})
              </span>
            ) : null}
          </div>

          <div className={styles.userInfo} ref={userInfoRef}>
            <div className={styles.userDetails}>
              <span className={styles.userEmail}>
                {user?.email || "No email"}
              </span>
              <span className={styles.userRole}>
                {roleLabels[user?.role] || "Unknown Role"}
              </span>
            </div>

            {/* Avatar + Copy Button */}
            <div className={styles.avatarContainer}>
              <div className={styles.userAvatar} onClick={toggleProfile}>
                <img
                  src={`https://ui-avatars.com/api/?name=${user?.name || "Guest"
                    }&background=0D8ABC&color=fff`}
                  alt="User Avatar"
                  className={styles.avatar}
                />
              </div>

              <button
                className={styles.copyIdButton}
                onClick={handleCopyUserId}
                title="Copy User ID"
                aria-label="Copy User ID"
              >
                <FaCopy />
              </button>

            </div>

            <div className={styles.notification} onClick={toggleNotifications}>
              <Bell className={styles.bellIcon} />
            </div>
            {role === "vendor" ? (
              <div className={styles.notification} onClick={toggleCredit}>
                <FaMoneyCheckDollar className={styles.bellIcon} />
              </div>
            ) : null}

            {showNotifications && (
              <div className={styles.popup}>
                <h2>Notifications</h2>
                {notifications.length > 0 ? (
                  <ul className={styles.notificationList}>
                    <RequestsComponent />
                  </ul>
                ) : (
                  <p className={styles.noNotifications}>No notifications</p>
                )}
              </div>
            )}

            {showProfile && <ProfileDropDown setShowProfile={setShowProfile} />}

            {showCredit && (
              <div className={styles.popup}>
                <div className={styles.creditDetails}>
                  {isLoading ? (
                    <LoaderSpiner />
                  ) : error ? (
                    <>
                      <p className={styles.error}>{error}</p>
                      <button
                        className={styles.retryButton}
                        onClick={handleRetry}
                      >
                        Retry
                      </button>
                    </>
                  ) : !rolebased ? (
                    <p className={styles.error}>
                      Credit information is only available for Vendors and
                      Participants.
                    </p>
                  ) : !authData.token ? (
                    <p className={styles.error}>
                      Authentication token missing. Please log in again.
                    </p>
                  ) : (
                    <>
                      <div className={styles.balanceSection}>
                        <Link
                          to="/BalanceStatement"
                          onClick={() => setShowCredit(false)}
                          className={styles.balanceAmount}
                        >
                          ₹{balanceData.balance?.toFixed(2)}
                        </Link>
                        <p className={styles.balanceLabel}>Current Balance</p>
                      </div>

                      <p className={styles.rechargeInfo}>
                        Total Recharges:{" "}
                        <strong>{balanceData.totalRecharges}</strong> <br />
                        Total Recharged Amount:{" "}
                        <strong>₹{balanceData.totalRechargedAmount}</strong>
                      </p>
                    </>
                  )}
                </div>
              </div>
            )}
            {role === "vendor" && (
              <div className={styles.balanceSection}>
                <Link
                  to="/BalanceStatement"
                  onClick={() => setShowCredit(false)}
                  className={styles.balanceAmount}
                >
                  ₹{balanceData.balance?.toFixed(2)}
                </Link>
              </div>
            )}
          </div>
        </div>
      </header>
    </>
  );
};

export default memo(HeaderBar);